import { format, getHours, getMinutes } from "date-fns";
import { CATEGORY_ADVANCED, CATEGORY_SUBSCRIBER } from "@config/config";

function minutesSinceMidnight(date) {
    return getHours(date) * 60 + getMinutes(date);
}

function normalizeTimeString(dateTime) {
    if (dateTime instanceof Date) {
        return dateTime;
    }

    if (typeof dateTime === "string") {
        return new Date(dateTime);
    }

    if (dateTime?.date) {
        return new Date(dateTime.date);
    }

    return null;
}

// Rule times are local time at the venue, not absolute instants - parse HH:mm directly
// so we never let new Date(...).getHours() convert through the runtime's own timezone/DST.
function parseWallClockMinutes(isoString) {
    if (typeof isoString !== "string") {
        return null;
    }

    const match = /T(\d{2}):(\d{2})/.exec(isoString);
    if (!match) {
        return null;
    }

    return Number(match[1]) * 60 + Number(match[2]);
}

// Day/time-aware lookup

export const findTicketPriceRule = (promoPrices, ticketClassId, session) => {
    if (!Array.isArray(promoPrices) || promoPrices.length === 0 || !session) {
        return null;
    }

    const sessionDateTime = normalizeTimeString(session?.session_date);
    if (!sessionDateTime) {
        return null;
    }

    const sessionTime = minutesSinceMidnight(sessionDateTime);
    const sessionDay = format(sessionDateTime, "EEEE").toLowerCase();

    return promoPrices.find((rule) => {
        const ruleStart = parseWallClockMinutes(rule.start_time);
        const ruleEnd = parseWallClockMinutes(rule.end_time);

        return (
            rule.ticket_class_id === ticketClassId &&
            ruleStart !== null &&
            ruleEnd !== null &&
            ruleStart <= sessionTime &&
            ruleEnd >= sessionTime &&
            rule[sessionDay]
        );
    }) || null;
};

export const isSessionValidPromo = (promo, session) => {
    if (!promo || !session) {
        return null;
    }

    const ticketClass = session.ticket_class_id;
    const hasTicketPriceRules = Array.isArray(promo?.promo_code_ticket_price)
        && promo.promo_code_ticket_price.length > 0;

    // Movie club / membership-style promo: no time/day rules, just ticket-class eligibility.
    if (!hasTicketPriceRules) {
        const eligible = Array.isArray(promo?.ticket_classes)
            ? promo.ticket_classes.includes(ticketClass)
            : promo?.ticket_class_id === ticketClass;

        return eligible ? { adult_price_cents: null } : null;
        // adult_price_cents: null tells the caller "use session.ticket_price_cents", see below
    }

    return findTicketPriceRule(promo.promo_code_ticket_price, ticketClass, session);
};

// Filters promo relevant promo options for the Redemptions Bar/Select component
export const filterPromoOptions = (promoOptions, session) => {
    const ticketClass = session?.ticket_class_id;
    if (promoOptions?.length === 0 || !ticketClass) {
        return [];
    }

    const sessionDateTime = normalizeTimeString(session?.session_date);

    return promoOptions.filter((option) => {
        if (option.promo_category === CATEGORY_ADVANCED) {
            const hasTicketPriceRules = Array.isArray(option?.promo_code_ticket_price)
                && option.promo_code_ticket_price.length > 0;

            if (!hasTicketPriceRules) {
                // Movie club / membership promo: eligibility by ticket class only.
                return Array.isArray(option?.ticket_classes)
                    ? option.ticket_classes.includes(ticketClass)
                    : option?.ticket_class_id === ticketClass;
            }

            if (!sessionDateTime) return false;
            const sessionTime = minutesSinceMidnight(sessionDateTime);
            const sessionDay = format(sessionDateTime, "EEEE").toLowerCase();

            return option.promo_code_ticket_price.some((rule) => {
                const ruleStart = parseWallClockMinutes(rule.start_time);
                const ruleEnd = parseWallClockMinutes(rule.end_time);

                return (
                    rule.ticket_class_id === ticketClass &&
                    ruleStart !== null &&
                    ruleEnd !== null &&
                    ruleStart <= sessionTime &&
                    ruleEnd >= sessionTime &&
                    rule[sessionDay]
                );
            });
        }

        if (option.promo_category === CATEGORY_SUBSCRIBER) {
            return option?.ticket_class_id === ticketClass && !option?.is_over_limit;
        }
        return true;
    });
};

// Determines if there is only one valid promo option to display OR if there is a promo with capped purchases (which must be displayed).
export const findPriorityPromo = (promoOptions) => {
    const capReachedOption = promoOptions.find(item =>item.cap_purchases);

    if (capReachedOption) {
        return capReachedOption;
    }

    if (promoOptions.length === 1) {
        return promoOptions[0];
    }

    return null;
};
export const isAutoSelected = (promo) => {
    if (!promo) {
        return null;
    }

    if (promo.promo_category === CATEGORY_ADVANCED) {
        return (
            promo.cap_purchases ||
            promo.promo_reuse_count < promo.reuse_limit
        )
            ? promo
            : null;
    }

    if (promo.promo_category === CATEGORY_SUBSCRIBER) {
        return promo;
    }

    return null;
};
